import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  CcdlDialogService,
  CcdlModalService,
  CcdlToastService,
  ModalOptions,
  ModalRef,
  ToastRef,
} from '@cpm-pr1618001/ng-design-lib';
import {
  Countries,
  DashboardLabelsFacade,
  HttpRequestStatus,
} from '@cpm-pr1618003/ng-initializer-lib';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  getTrustedCaptrixonOrigins,
  redirectToTrustedUrl,
} from '../../utils/redirect.utils';
import { UserDialogView } from '../landing-page.modal';
import { LandingPageFacade } from '../store/facade/landing-page.facade';
import {
  FPDetails,
  SignInDetails,
  SignInResponse,
  SignUpDetails,
  SignUpResponse,
} from '../store/state/landing-page.state';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { ResetUpdateComponent } from './reset-update/reset-update.component';
import { SignInComponent } from './sign-in/sign-in.component';
import { SignUpComponent } from './sign-up/sign-up.component';
import { TermsComponent } from './terms/terms.component';
import { UserPresenter } from './user.presenter';

const trustedCaptrixonOrigins: string[] = getTrustedCaptrixonOrigins(
  environment.captrixonURL,
);

@Component({
  selector: 'app-user',
  templateUrl: './user.container.component.html',
  styleUrls: ['./user.container.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SignInComponent,
    SignUpComponent,
    ResetUpdateComponent,
    ForgotPasswordComponent,
    // MobileMenuComponent,
  ],
  standalone: true,
})
export class UserContainerComponent implements OnInit, OnDestroy {
  @Output() requestNavigateToView = new EventEmitter<UserDialogView>();

  activeSignButton$ = this.landingPageFacade.activeSignButton$;
  signInDetails$ = this.landingPageFacade.signInDetails$;
  signUpDetails$ = this.landingPageFacade.signUpDetails$;
  isOTPRequested$ = this.landingPageFacade.isOTPRequested$;
  isPasswordUpdated$ = this.landingPageFacade.isPasswordUpdated$;
  signInRequestStatus$ = this.landingPageFacade.signInRequestStatus$;
  signUpRequestStatus$ = this.landingPageFacade.signUpRequestStatus$;
  requestOTPRequestStatus$ = this.landingPageFacade.requestOTPRequestStatus$;
  changePasswordRequestStatus$ =
    this.landingPageFacade.changePasswordRequestStatus$;
  countries$ = this.dashboardLabelsFacade.countries$;
  states$ = this.dashboardLabelsFacade.statesOfSelectedCountry$;
  chosenCountry$ = this.dashboardLabelsFacade.chosenCountry$;

  isSignInSubmitTriggered: boolean = false;
  isSignUpSubmitTriggered: boolean = false;
  isFPSubmitTriggered: boolean = false;

  userForm!: FormGroup;

  private subscription: Subscription = new Subscription();

  chosenCountry: Countries;
  constructor(
    private landingPageFacade: LandingPageFacade,
    private dashboardLabelsFacade: DashboardLabelsFacade,
    private userPresenter: UserPresenter,
    private ccdlModalService: CcdlModalService,
    private ccdlDialogService: CcdlDialogService,
    private ccdlToastService: CcdlToastService,
    private router: Router,
  ) {
    this.userForm = this.userPresenter.initializeFormGroup();
  }

  ngOnInit(): void {
    this.subscription.add(
      this.signInDetails$.subscribe((details: SignInResponse) => {
        if (details && details.userId) {
          this.onSignInSuccess(details);
        }
      }),
    );

    this.subscription.add(
      this.signUpDetails$.subscribe((details: SignUpResponse) => {
        if (details) {
          this.userForm.reset();
          this.isSignUpSubmitTriggered = false;
        }
      }),
    );

    this.subscription.add(
      this.isPasswordUpdated$.subscribe((details: boolean) => {
        if (details) {
          this.userForm.reset();
          this.isFPSubmitTriggered = false;
        }
      }),
    );

    this.subscription.add(
      this.isOTPRequested$.subscribe((requested: boolean) => {
        if (requested) {
          this.isFPSubmitTriggered = false;
        }
      }),
    );

    this.subscription.add(
      this.chosenCountry$.subscribe((selectedCountry: Countries) => {
        this.chosenCountry = selectedCountry;
      }),
    );

    this.subscription.add(
      this.signInRequestStatus$
        .pipe(distinctUntilChanged())
        .subscribe((status: HttpRequestStatus) => {
          if (status === HttpRequestStatus.Failed) {
            this.openErrorToast(() =>
              this.landingPageFacade.resetSignInRequestStatus(),
            );
          }
        }),
    );

    this.subscription.add(
      this.signUpRequestStatus$
        .pipe(distinctUntilChanged())
        .subscribe((status: HttpRequestStatus) => {
          if (status === HttpRequestStatus.Failed) {
            this.openErrorToast(() =>
              this.landingPageFacade.resetSignUpRequestStatus(),
            );
          }
        }),
    );

    this.subscription.add(
      this.requestOTPRequestStatus$
        .pipe(distinctUntilChanged())
        .subscribe((status: HttpRequestStatus) => {
          if (status === HttpRequestStatus.Failed) {
            this.openErrorToast(() =>
              this.landingPageFacade.resetRequestOTPRequestStatus(),
            );
          }
        }),
    );

    this.subscription.add(
      this.changePasswordRequestStatus$
        .pipe(distinctUntilChanged())
        .subscribe((status: HttpRequestStatus) => {
          if (status === HttpRequestStatus.Failed) {
            this.openErrorToast(() =>
              this.landingPageFacade.resetChangePasswordRequestStatus(),
            );
          }
        }),
    );
  }

  onSignInSuccess(details: SignInResponse): void {
    const didRedirect = redirectToTrustedUrl(
      environment.captrixonURL,
      trustedCaptrixonOrigins,
    );
    if (!didRedirect) {
      this.router.navigate(['/error-page']);
    }
  }

  getFormGroup(formGroupName: string): FormGroup {
    return this.userForm?.get(formGroupName) as FormGroup;
  }

  forgotPassword() {
    this.isSignInSubmitTriggered = false;
    this.landingPageFacade.setActiveSignButton(UserDialogView.FORGOT_PASSWORD);
  }

  signUpView() {
    this.isSignInSubmitTriggered = false;
    this.landingPageFacade.setActiveSignButton(UserDialogView.SIGN_UP);
  }

  signInView(): void {
    this.isSignUpSubmitTriggered = false;
    this.isFPSubmitTriggered = false;
    this.landingPageFacade.setActiveSignButton(UserDialogView.SIGN_IN);
  }

  mobileSignInView(): void {
    this.requestNavigateToView.emit(UserDialogView.SIGN_IN);
  }

  mobileSignUpView(): void {
    this.requestNavigateToView.emit(UserDialogView.SIGN_UP);
  }

  closeDialogMenu(): void {
    this.ccdlDialogService.dismiss();
  }

  signInSubmit(): void {
    this.isSignInSubmitTriggered = true;
    const signInForm: AbstractControl = this.userForm.get('signInForm');
    if (signInForm.valid) {
      const signInDetails: SignInDetails =
        this.userPresenter.getSignInPayload();
      this.landingPageFacade.signIn(signInDetails);
    }
  }

  signUpSubmit(): void {
    this.isSignUpSubmitTriggered = true;
    const signUpForm: AbstractControl = this.userForm.get('signUpForm');
    const isRememberMeChecked: boolean = signUpForm.get('rememberMe').value;
    if (signUpForm.valid && isRememberMeChecked) {
      const signUpDetails: SignUpDetails = this.userPresenter.getSignUpPayload(
        this.chosenCountry,
      );
      this.landingPageFacade.signUp(signUpDetails);
    }
  }

  requestOTP(emailId: string): void {
    this.isFPSubmitTriggered = true;
    const emailControl = this.getFormGroup('fpForm').get('email');
    if (emailControl?.valid) {
      this.landingPageFacade.requestOTP(emailId);
    }
  }

  cancelFP(): void {
    this.isFPSubmitTriggered = false;
    this.landingPageFacade.setActiveSignButton(UserDialogView.FORGOT_PASSWORD);
  }

  resetPasswordSubmit(): void {
    this.isFPSubmitTriggered = true;
    const fpForm: AbstractControl = this.userForm.get('fpForm');
    if (fpForm.valid) {
      const fpDetails: FPDetails = this.userPresenter.getResetPasswordPayload();
      this.landingPageFacade.resetPassword(fpDetails);
    }
  }

  onTermsEmitter(): void {
    const options: ModalOptions = {
      headerText: 'Terms of Use',
      component: TermsComponent,
      modalButtons: [
        {
          btnLabel: 'Close',
          size: 'md',
          btnType: 'primary',
          actionType: 'CLOSE',
        },
      ],
      freezeIfDailogExists: true,
    };
    const modalRef: ModalRef = this.ccdlModalService.open(options);
    modalRef.result.finally(() => {});
  }

  onPrivacyEmitter(): void {
    const options: ModalOptions = {
      headerText: 'Privacy Policy',
      component: PrivacyPolicyComponent,
      modalButtons: [
        {
          btnLabel: 'Close',
          size: 'md',
          btnType: 'primary',
          actionType: 'CLOSE',
        },
      ],
      freezeIfDailogExists: true,
    };
    const modalRef: ModalRef = this.ccdlModalService.open(options);
    modalRef.result.finally(() => {});
  }

  selectedCountry(countryCode: string): void {
    this.dashboardLabelsFacade.chosenCountry(countryCode);
  }

  selectedState(stateCode: string): void {
    this.dashboardLabelsFacade.chosenState(stateCode);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private openErrorToast(onClose: () => void): void {
    const toastRef: ToastRef = this.ccdlToastService.open({
      title: 'Something went wrong',
      message: "We're unable to process your request. Please try again.",
      type: 'error',
      timeout: 5,
    });
    toastRef.result.finally(() => {
      onClose();
    });
  }
}
